import { Menu } from "@headlessui/react";
import clsx from "clsx";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { MoreVertical } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useInvalidateBasket } from "@/services/api";

type Props = {
  basketId?: number;
  onBasketDeleted: () => void;
};

export const AddDeviceOverflowMenu: FC<Props> = ({
  basketId,
  onBasketDeleted,
}) => {
  const { t } = useTranslation("portal", { keyPrefix: "add-device" });
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutateAsync: invalidateBasket, isPending: isPending } =
    useInvalidateBasket();

  const handleCloseDeleteModal = () => {
    if (!isPending) {
      setIsDeleteModalOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (basketId) {
      await invalidateBasket({ basketId });
    }

    onBasketDeleted();
    navigate(portalRoutes.cockpit.base, { replace: true });
  };

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button
          data-testid="add-device-overflow-menu"
          className="group relative flex size-8 items-center justify-center rounded-md text-foreground-primary focus:outline-none"
          aria-label={t("overflowMenuLabel")}
        >
          <MoreVertical className="size-4" />
          <FocusRing />
        </Menu.Button>
        <Menu.Items
          className="absolute right-0 top-9 z-10 flex w-fit flex-col gap-1 rounded-lg bg-surface-primary p-2 focus:outline-none"
          modal={false}
        >
          <Menu.Item>
            {({ active, close }) => (
              <button
                className={clsx(
                  "w-full whitespace-nowrap rounded-md px-3 py-2.5 text-left text-body-base !font-semibold leading-none text-foreground-primary-inverse",
                  active && "bg-surface-tertiary-inverse",
                )}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  close();
                  setIsDeleteModalOpen(true);
                }}
              >
                {tDefault("Common.label.delete")}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>

      {isDeleteModalOpen && (
        <Modal
          title={t("deleteBasket.title")}
          onClose={handleCloseDeleteModal}
          hasScroll={false}
          variant="dark"
        >
          <ButtonGroupFlexBox
            className="!mt-0"
            rightAligned
            forceButtonWidth
            primaryButton={
              <Button
                dataTestid="add-device-delete-basket-confirm"
                variant="primary"
                inverted
                loading={isPending}
                onClick={() => void handleConfirmDelete()}
              >
                {tDefault("Common.label.delete")}
              </Button>
            }
            secondaryButton={
              <Button
                variant="secondary"
                inverted
                disabled={isPending}
                onClick={handleCloseDeleteModal}
              >
                {tDefault("Common.label.cancel")}
              </Button>
            }
          />
        </Modal>
      )}
    </>
  );
};
